import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@mantine/core';
import { IconCircleCheck, IconShieldCheck } from '@tabler/icons-react';
import { RIDES_SECURITY_CHECK_TYPE, useRidesSecurityService } from '@actschurch/ccms-sdk';
import { errorMessage } from '@/lib/copy';
import { formatStamp } from '@/lib/format';
import { Callout } from '@/components/ui';
import '@/styles/member.css';

const UNDO_SECONDS = 5;

interface Props {
  rideAvailabilityID: number;
  driverName: string;
  /** pickup = this campus is where the driver collects riders; dropoff = where they arrive. */
  pickup: boolean;
  /** yyyyMMddHHmmss of the recorded check, if any. */
  checkedAt?: number | null;
  onSaved: () => void;
}

/**
 * The big "Checked at pick-up" / "Checked at drop-off" button. A tap shows "Checked" straight away
 * with Undo for five seconds, then records it, so a mis-tap while scrolling a busy list costs
 * nothing. Leaving the page during those seconds still records it.
 */
export function CheckAction({ rideAvailabilityID, driverName, pickup, checkedAt, onSaved }: Props) {
  const security = useRidesSecurityService();
  const [pending, setPending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(UNDO_SECONDS);
  const timer = useRef<number | null>(null);
  const pendingRef = useRef(false);
  const checkTypeID = pickup ? RIDES_SECURITY_CHECK_TYPE.Pickup : RIDES_SECURITY_CHECK_TYPE.DropOff;
  const where = pickup ? 'pick-up' : 'drop-off';

  const record = useMutation({
    mutationFn: () => security.recordCheck({ rideAvailabilityID, checkTypeID, passed: true, notes: null }),
    onSuccess: () => onSaved(),
  });

  const clear = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
  };

  const start = () => {
    record.reset();
    setPending(true);
    pendingRef.current = true;
    setSecondsLeft(UNDO_SECONDS);
    clear();
    timer.current = window.setInterval(() => setSecondsLeft((n) => Math.max(0, n - 1)), 1000);
  };

  // The undo window has run out: record the check.
  useEffect(() => {
    if (!pending || secondsLeft > 0) return;
    clear();
    pendingRef.current = false;
    setPending(false);
    record.mutate();
  }, [pending, secondsLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  const undo = () => {
    clear();
    pendingRef.current = false;
    setPending(false);
  };

  // Leaving the page (or changing campus) during the undo window still records the check.
  useEffect(() => () => {
    clear();
    if (pendingRef.current) {
      void security.recordCheck({ rideAvailabilityID, checkTypeID, passed: true, notes: null }).catch(() => undefined);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (checkedAt) {
    return (
      <div className="rm-done" role="status">
        <IconCircleCheck size={24} aria-hidden="true" />
        <span className="txt">Checked at {where}<small>{formatStamp(checkedAt)}</small></span>
      </div>
    );
  }

  if (pending || record.isPending || record.isSuccess) {
    return (
      <div className="rm-done" role="status" aria-live="polite">
        <IconCircleCheck size={24} aria-hidden="true" />
        <span className="txt">
          Checked at {where}
          <small>{pending ? `Saving in ${secondsLeft} seconds` : record.isPending ? 'Saving…' : 'Saved'}</small>
        </span>
        {pending && <button type="button" onClick={undo} aria-label={`Undo the ${where} check for ${driverName}`}>Undo</button>}
      </div>
    );
  }

  return (
    <>
      <Button size="lg" fullWidth onClick={start} leftSection={<IconShieldCheck size={22} />}>
        Checked at {where}
      </Button>
      {record.error && <Callout tone="bad" live>{errorMessage(record.error, "We couldn't save the check. Please try again.")}</Callout>}
    </>
  );
}
